const STORAGE_KEY = 'rancold-vat-mode';
const VALID_MODES = new Set(['excl', 'incl']);

function readMode() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return VALID_MODES.has(stored) ? stored : 'excl';
  } catch (_error) {
    return 'excl';
  }
}

function writeMode(mode) {
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch (_error) {
    // Browsers with blocked storage still retain the selected mode for this page.
  }
}

let currentMode = readMode();

function applyPrice(element) {
  if (!(element instanceof HTMLElement)) return;
  const value = currentMode === 'incl' ? element.dataset.rcPriceIncl : element.dataset.rcPriceExcl;
  if (value) element.textContent = value;
}

function apply(root = document) {
  if (root instanceof HTMLElement && root.matches('[data-rc-price-excl]')) applyPrice(root);
  root.querySelectorAll?.('[data-rc-price-excl]').forEach(applyPrice);

  document.documentElement.dataset.rcVatMode = currentMode;
  document.querySelectorAll('[data-rc-vat-mode]').forEach((button) => {
    const active = button.dataset.rcVatMode === currentMode;
    button.setAttribute('aria-pressed', String(active));
  });
  document.querySelectorAll('[data-rc-vat-copy]').forEach((label) => {
    label.textContent = currentMode === 'incl' ? 'Inkl. moms' : 'Exkl. moms';
  });
}

function setMode(mode) {
  if (!VALID_MODES.has(mode)) return;
  currentMode = mode;
  writeMode(mode);
  apply();
  document.dispatchEvent(new CustomEvent('rancold:vat-change', { detail: { mode } }));
}

window.RancoldVAT = { apply, getMode: () => currentMode, setMode };

document.addEventListener('click', (event) => {
  const trigger = event.target.closest?.('[data-rc-vat-mode]');
  if (trigger) setMode(trigger.dataset.rcVatMode);
});

const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    mutation.addedNodes.forEach((node) => {
      if (node instanceof HTMLElement) apply(node);
    });
  }
});

function init() {
  apply();
  observer.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

class RCCollectionCarousel extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.slides = [...this.querySelectorAll('[data-rc-carousel-slide]')];
    this.shell = this.closest('.rc-mega-shop-layout');
    this.targets = [...(this.shell?.querySelectorAll('[data-rc-carousel-target]') || [])];
    this.previousButton = this.querySelector('[data-rc-carousel-prev]');
    this.nextButton = this.querySelector('[data-rc-carousel-next]');
    this.currentLabel = this.querySelector('[data-rc-carousel-current]');
    this.totalLabel = this.querySelector('[data-rc-carousel-total]');
    this.activeIndex = 0;
    this.timer = null;

    if (!this.slides.length) {
      this.hidden = true;
      return;
    }

    this.totalLabel.textContent = String(this.slides.length);
    const controlsAreUseful = this.slides.length > 1;
    this.previousButton.hidden = !controlsAreUseful;
    this.nextButton.hidden = !controlsAreUseful;

    this.previousButton.addEventListener('click', () => this.previous());
    this.nextButton.addEventListener('click', () => this.next());

    this.targets.forEach((target) => {
      const showTargetSlide = () => {
        const sourceIndex = target.dataset.rcCarouselTarget;
        const slideIndex = this.slides.findIndex((slide) => slide.dataset.slideIndex === sourceIndex);
        if (slideIndex < 0) return;
        this.stopAutoplay();
        this.goTo(slideIndex);
      };

      target.addEventListener('pointerenter', showTargetSlide);
      target.addEventListener('focus', showTargetSlide);
      target.addEventListener('pointerleave', () => this.startAutoplay());
      target.addEventListener('blur', () => this.startAutoplay());
    });

    this.addEventListener('click', (event) => {
      const slide = event.target.closest?.('[data-rc-carousel-slide]');
      if (!slide) return;

      const clickedIndex = this.slides.indexOf(slide);
      if (clickedIndex === this.activeIndex) return;

      event.preventDefault();
      this.goTo(clickedIndex);
    });

    this.addEventListener('pointerenter', () => this.stopAutoplay());
    this.addEventListener('pointerleave', () => this.startAutoplay());
    this.addEventListener('focusin', () => this.stopAutoplay());
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.startAutoplay();
    });

    this.visibilityHandler = () => {
      if (document.hidden) this.stopAutoplay();
      else this.startAutoplay();
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);

    this.render();
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }
  }

  goTo(index) {
    if (!this.slides.length) return;
    this.activeIndex = (index + this.slides.length) % this.slides.length;
    this.render();
    this.restartAutoplay();
  }

  previous() {
    this.goTo(this.activeIndex - 1);
  }

  next() {
    this.goTo(this.activeIndex + 1);
  }

  render() {
    const total = this.slides.length;

    this.slides.forEach((slide, index) => {
      let offset = index - this.activeIndex;
      if (offset > total / 2) offset -= total;
      if (offset < -total / 2) offset += total;

      const isActive = offset === 0;
      const isPrevious = offset === -1;
      const isNext = offset === 1;
      const isVisible = isActive || isPrevious || isNext;

      slide.classList.toggle('is-active', isActive);
      slide.classList.toggle('is-prev', isPrevious);
      slide.classList.toggle('is-next', isNext);
      slide.setAttribute('aria-hidden', String(!isVisible));
      slide.tabIndex = isActive ? 0 : -1;

      if (isActive) slide.setAttribute('aria-current', 'true');
      else slide.removeAttribute('aria-current');
    });

    const activeSourceIndex = this.slides[this.activeIndex]?.dataset.slideIndex;
    this.targets.forEach((target) => {
      target.classList.toggle('is-active', target.dataset.rcCarouselTarget === activeSourceIndex);
    });

    this.currentLabel.textContent = String(this.activeIndex + 1);
  }

  wantsAutoplay() {
    return (
      this.dataset.autoplay === 'true' &&
      this.slides.length > 1 &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  startAutoplay() {
    if (
      !this.wantsAutoplay() ||
      this.timer ||
      this.shell?.matches(':hover') ||
      this.shell?.contains(document.activeElement)
    ) return;
    const speed = Math.max(Number(this.dataset.speed) || 6000, 4000);
    this.timer = window.setInterval(() => this.next(), speed);
  }

  stopAutoplay() {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }
}

if (!customElements.get('rc-collection-carousel')) {
  customElements.define('rc-collection-carousel', RCCollectionCarousel);
}
